/** Crawler policy page — public transparency surface required by the
 *  Credible Crawl Engine epic (Part 6). Any station operator worried about
 *  the bot in their access log can land here and see exactly what
 *  WaveScorePro is doing, at what cadence, and how to contact us.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { T } from "@/lib/theme";

const C = T.dark;
const API = process.env.REACT_APP_BACKEND_URL;

export default function Crawler() {
  const nav = useNavigate();
  const [policy, setPolicy] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/api/public/crawler`)
      .then((r) => setPolicy(r.data))
      .catch((e) => setError(e.message || "Unable to load policy"));
  }, []);

  const kv = (k, v, testId) => (
    <div
      data-testid={testId}
      style={{
        display: "flex", justifyContent: "space-between", gap: 16,
        padding: "10px 0", borderBottom: `1px solid ${C.border}`,
      }}>
      <span style={{ color: C.muted, fontSize: 13 }}>{k}</span>
      <span style={{ color: C.text, fontSize: 13, fontFamily: "'IBM Plex Mono',monospace", textAlign: "right" }}>
        {v}
      </span>
    </div>
  );

  return (
    <div data-testid="crawler-page" style={{ background: C.bg, minHeight: "100vh", color: C.text, fontFamily: "'Inter',sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@700;800&display=swap'); body{margin:0;background:${C.bg}}`}</style>

      <div style={{ background: C.surface, borderBottom: `1px solid ${C.border}`, padding: "14px 28px",
        display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Link to="/" style={{ textDecoration: "none", color: C.text, fontWeight: 800, fontSize: 18 }}>
          WaveScore<span style={{ color: T.greenAccent }}>Pro</span>
        </Link>
        <button data-testid="crawler-back" onClick={() => nav(-1)}
          style={{ background: "transparent", color: C.text, border: `1px solid ${C.border}`,
            padding: "6px 14px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
          ← Back
        </button>
      </div>

      <div style={{ maxWidth: 780, margin: "0 auto", padding: "48px 28px 80px" }}>
        <div style={{ color: C.muted, fontSize: 11, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 10 }}>
          Crawler · public policy
        </div>
        <h1 style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 38, fontWeight: 800, margin: "0 0 14px", letterSpacing: "-0.02em" }}>
          Our crawler policy
        </h1>
        <p style={{ color: C.muted, fontSize: 15, lineHeight: 1.7, margin: "0 0 36px", maxWidth: 680 }}>
          WaveScorePro publishes only directly measured digital audience numbers.
          To do that we probe public Icecast / Shoutcast stats endpoints on the
          open web. This page documents exactly what we do — so any operator
          worried about the bot in their access log can verify our behaviour
          and contact us if they'd like to be excluded.
        </p>

        {error && (
          <div style={{ color: "#F87171", padding: 12, background: "rgba(248,113,113,0.08)",
            border: "1px solid rgba(248,113,113,0.35)", borderRadius: 8, marginBottom: 24 }}>
            {error}
          </div>
        )}

        {policy && (
          <>
            <section data-testid="crawler-identity" style={{ marginBottom: 32, padding: 22,
              background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12 }}>
              <h3 style={{ margin: "0 0 14px", fontSize: 17, fontWeight: 700 }}>How to identify us</h3>
              {kv("User-Agent", policy.user_agent, "crawler-user-agent")}
              {kv("Contact", <a href={`mailto:${policy.contact_email}`} style={{ color: T.greenAccent }}>{policy.contact_email}</a>, "crawler-contact")}
              {kv("robots.txt", "Honoured — cached per host", "crawler-robots")}
              {kv("Rate limit", `1 request / ${policy.policy.min_interval_seconds}s per host`, "crawler-rate")}
              {kv("Backoff on 429/5xx", `${policy.policy.backoff_base_seconds}s → ${policy.policy.backoff_max_seconds}s (exponential)`)}
            </section>

            <section data-testid="crawler-endpoints" style={{ marginBottom: 32, padding: 22,
              background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12 }}>
              <h3 style={{ margin: "0 0 14px", fontSize: 17, fontWeight: 700 }}>Endpoints we probe</h3>
              <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.65, margin: "0 0 12px" }}>
                We only read the endpoints your streaming server publishes
                for anyone. We do not attempt authentication, do not scrape
                admin panels, do not download audio for storage.
              </p>
              <ul style={{ margin: 0, paddingLeft: 20, color: C.muted, fontSize: 13, lineHeight: 1.9 }}>
                {policy.policy.endpoints_probed.map((e) => (
                  <li key={e}><code style={{ color: T.greenAccent }}>{e}</code></li>
                ))}
              </ul>
            </section>

            <section data-testid="crawler-anti-gaming" style={{ marginBottom: 32, padding: 22,
              background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12 }}>
              <h3 style={{ margin: "0 0 14px", fontSize: 17, fontWeight: 700 }}>Anti-gaming sanity layer</h3>
              <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.65, margin: "0 0 12px" }}>
                Every crawled number passes through three defences before
                it appears on any public surface:
              </p>
              <ul style={{ margin: 0, paddingLeft: 20, color: C.muted, fontSize: 13, lineHeight: 1.9 }}>
                <li><strong>Endpoint authenticity</strong> — the response must parse as a structurally valid Icecast/Shoutcast payload.</li>
                <li><strong>Endpoint dedupe</strong> — if two stations point at the same mount-point, only the first-verified one counts. Cloning a station page cannot inflate two rows off one stream.</li>
                <li><strong>Spike freeze</strong> — a listener count more than <strong>{policy.policy.anti_gaming.spike_multiplier}×</strong> the trailing {policy.policy.anti_gaming.baseline_window_hours}h mean is frozen to the 90-day P95 and flagged for review.</li>
              </ul>
            </section>

            <section data-testid="crawler-coverage" style={{ marginBottom: 32, padding: 22,
              background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12 }}>
              <h3 style={{ margin: "0 0 14px", fontSize: 17, fontWeight: 700 }}>Coverage right now</h3>
              {kv("Stations known", policy.coverage.stations_known.toLocaleString(), "crawler-known")}
              {kv("Currently measured (DIRECT)", policy.coverage.stations_measured_now.toLocaleString(), "crawler-measured")}
              {kv("Coverage ratio", `${Math.round(policy.coverage.ratio * 100)}%`, "crawler-ratio")}
              {kv("Hosts tracked (crawl-loop)", policy.live.hosts_tracked, "crawler-hosts")}
              {kv("Hosts in backoff", policy.live.hosts_in_backoff, "crawler-backoff")}
            </section>

            <section data-testid="crawler-exclude" style={{ marginBottom: 32, padding: 22,
              background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12 }}>
              <h3 style={{ margin: "0 0 14px", fontSize: 17, fontWeight: 700 }}>How to exclude us</h3>
              <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.65, margin: "0 0 8px" }}>
                Add this to your <code style={{ color: T.greenAccent }}>robots.txt</code>:
              </p>
              <pre style={{ background: "#0A1628", padding: 14, borderRadius: 8, fontSize: 12, color: T.greenAccent,
                fontFamily: "'IBM Plex Mono',monospace", overflow: "auto", margin: 0 }}>
{`User-agent: WaveScorePro-Crawler
Disallow: /`}
              </pre>
              <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.65, margin: "12px 0 0" }}>
                Or email <a href={`mailto:${policy.contact_email}`} style={{ color: T.greenAccent }}>{policy.contact_email}</a> and
                we'll add a manual block within 24h.
              </p>
            </section>
          </>
        )}
      </div>

      <footer style={{ borderTop: `1px solid ${C.border}`, padding: "24px 28px", textAlign: "center", color: C.muted, fontSize: 12 }}>
        © {new Date().getFullYear()} I-Assist Pro Limited · Accra, Ghana
      </footer>
    </div>
  );
}
